import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Mail, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

/**
 * Reusable "Email PDF" dialog.
 *
 * Props:
 *   - trigger: React node used as the trigger button (defaults to an outline Email button)
 *   - endpoint: string — the POST endpoint to hit, e.g. `/bills/{id}/email`
 *   - queryParams?: object — extra params merged into the URL (used for date_from/date_to)
 *   - defaultTo?: string — pre-fill recipient
 *   - defaultSubject?: string
 *   - defaultMessage?: string
 *   - buttonLabel?: string  — the outline trigger button label (default "Email")
 *   - testIdPrefix?: string — unique prefix for data-testids
 */
export default function EmailDialog({
  trigger,
  endpoint,
  queryParams = {},
  defaultTo = "",
  defaultSubject = "",
  defaultMessage = "",
  buttonLabel = "Email",
  testIdPrefix = "email",
}) {
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState(defaultTo);
  const [subject, setSubject] = useState(defaultSubject);
  const [message, setMessage] = useState(defaultMessage);
  const [sending, setSending] = useState(false);
  const [smtpReady, setSmtpReady] = useState(null); // null unknown, true, false

  useEffect(() => {
    if (!open) return;
    setTo(defaultTo); setSubject(defaultSubject); setMessage(defaultMessage);
    // Best-effort probe of SMTP config so we can guide the user before they hit Send.
    api.get("/settings").then((r) => setSmtpReady(!!r.data?.smtp_configured))
       .catch(() => setSmtpReady(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const send = async () => {
    if (!to || !to.includes("@")) { toast.error("Please enter a valid recipient email"); return; }
    setSending(true);
    try {
      await api.post(endpoint, { to: to.trim(), subject, message }, { params: queryParams });
      toast.success(`Email sent to ${to}`);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send email");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <span onClick={() => setOpen(true)} className="inline-flex">
        {trigger || (
          <Button variant="outline" className="rounded-sm" data-testid={`${testIdPrefix}-open-btn`}>
            <Mail className="h-3.5 w-3.5 mr-1.5" /> {buttonLabel}
          </Button>
        )}
      </span>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Send Email</DialogTitle>
            <div className="text-xs text-brand-muted">Sent from your Gmail via secure SMTP with the attached PDF.</div>
          </DialogHeader>
          {smtpReady === false && (
            <div className="text-xs bg-brand-sand/15 border border-brand-sand/40 rounded-sm px-3 py-2 text-brand-ink">
              Gmail is not configured yet.{" "}
              <Link to="/settings" onClick={() => setOpen(false)} className="underline font-medium">
                Open Settings
              </Link>{" "}
              and enter your Gmail address + 16-character App Password.
            </div>
          )}
          <div className="space-y-4">
            <div>
              <Label>To *</Label>
              <Input type="email" data-testid={`${testIdPrefix}-to`}
                value={to} onChange={(e) => setTo(e.target.value)}
                className="mt-1.5 h-10 rounded-sm bg-white"
                placeholder="party@example.com" />
            </div>
            <div>
              <Label>Subject</Label>
              <Input data-testid={`${testIdPrefix}-subject`}
                value={subject} onChange={(e) => setSubject(e.target.value)}
                className="mt-1.5 h-10 rounded-sm bg-white"
                placeholder="(leave blank for auto-generated subject)" />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea data-testid={`${testIdPrefix}-message`}
                value={message} onChange={(e) => setMessage(e.target.value)}
                className="mt-1.5 min-h-[110px] rounded-sm bg-white"
                placeholder="(leave blank for auto-generated message)" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm" disabled={sending}>Cancel</Button>
            <Button onClick={send} disabled={sending || smtpReady === false}
              data-testid={`${testIdPrefix}-send-btn`}
              className="rounded-sm bg-brand-forest text-white">
              {sending
                ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Sending…</>
                : <><Send className="h-3.5 w-3.5 mr-1.5" /> Send</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
